import { useCallback, useMemo } from "react";
import type { StreamEntry } from "../ChartSpec";
import { useDataViewerStore } from "../data-viewer-provider";

export type PanelFocus = {
    /** The spec entry, or undefined when nothing is focused or the stored id left the spec. */
    focusedStream: StreamEntry | undefined;
    /** Focuses the stream, or clears focus when it is already the focused one. */
    toggleFocus: (streamId: number) => void;
    clearFocus: () => void;
};

export function useManagePanelFocus(panelId: string, streams: StreamEntry[]): PanelFocus {
    const focusedStreamId = useDataViewerStore((state) => state.focusedStreams[panelId]);
    const setFocusedStream = useDataViewerStore((state) => state.setFocusedStream);

    const focusedStream = useMemo(
        () => streams.find((stream) => stream.streamId === focusedStreamId),
        [streams, focusedStreamId],
    );

    const toggleFocus = useCallback(
        (streamId: number) => setFocusedStream(panelId, streamId === focusedStream?.streamId ? null : streamId),
        [panelId, focusedStream, setFocusedStream],
    );

    const clearFocus = useCallback(() => setFocusedStream(panelId, null), [panelId, setFocusedStream]);

    return { focusedStream, toggleFocus, clearFocus };
}
